import React, { useState } from 'react';
import '../styles/ApplicationForm.css';
import MembershipOptions from './MembershipOptions';
import LinealForm from './LinealForm';
import MemorialForm from './MemorialForm';
import '../styles/Forms.css';
import config from '../config/environment';
import s3UploadService from '../services/s3UploadService';

// API endpoint for Lambda function - now comes from environment config
const LAMBDA_API_ENDPOINT = config.API_ENDPOINT || 'https://g6dvrvndnh.execute-api.us-east-1.amazonaws.com/prod';

// Updated file size limits - much larger now that we're using direct S3 uploads
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file (was 50MB)
const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB total (was 200MB)

// Function to create a form submission iframe
function createSubmissionIframe() {
  const iframe = document.createElement('iframe');
  iframe.name = 'form-submission-frame';
  iframe.style.display = 'none';
  document.body.appendChild(iframe);
  return iframe;
}

const ApplicationForm = () => {
  const [formType, setFormType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const handleFormTypeSelection = (type) => {
    setFormType(type);
    // Reset submission status when changing form type
    setSubmissionResult(null);
    setUploadProgress(null);
  };

  // Check if files exceed size limits (updated for larger limits)
  const validateFileSize = (files) => {
    let totalSize = 0;
    let oversizedFiles = [];
    
    for (const category in files) {
      for (const file of files[category]) {
        totalSize += file.size;
          
        if (file.size > MAX_FILE_SIZE) {
            oversizedFiles.push({
              name: file.name,
            size: file.size,
              category
            });
        }
      }
    }
    
    return {
      valid: totalSize <= MAX_TOTAL_SIZE && oversizedFiles.length === 0,
      totalSize,
      oversizedFiles
    };
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setSubmissionResult(null);
    setUploadProgress(null);
    
    try {
      // Validate file sizes with new limits
      const sizeValidation = validateFileSize(formData.documentFiles);
      
      if (!sizeValidation.valid) {
        if (sizeValidation.oversizedFiles.length > 0) {
          throw new Error(`Some files exceed the 10MB size limit: ${sizeValidation.oversizedFiles.map(f => `${f.name} (${formatFileSize(f.size)})`).join(', ')}. Please reduce file sizes and try again.`);
        } else if (sizeValidation.totalSize > MAX_TOTAL_SIZE) {
          throw new Error(`Total file size (${formatFileSize(sizeValidation.totalSize)}) exceeds the 100MB limit. Please reduce file sizes and try again.`);
        }
      }
      
      // Check if there are any files to upload
      const hasFiles = Object.values(formData.documentFiles).some(files => files && files.length > 0);
      
      let fileReferences = {};
      let uploadInfo = null;

      if (hasFiles) {
        console.log('Starting file upload process...');
        
        // Step 1: Upload files using S3 pre-signed URLs
        try {
          const uploadResult = await s3UploadService.uploadFiles(
            formData.qualifyingVeteran.name,
            formData.documentFiles,
            (progress) => {
              setUploadProgress(progress);
            },
            formData.formType  // Add form type for folder naming with prefix
          );
          
          fileReferences = uploadResult.fileReferences;
          uploadInfo = uploadResult.uploadInfo;
          
          console.log('File upload completed successfully');
          
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
      } else {
        console.log('No files to upload, proceeding with form submission');
      }
      
      // Update progress to show form submission phase
      setUploadProgress({
        stage: 'submitting',
        message: 'Submitting form data...',
        percentage: 100
      });
      
      // Step 2: Submit form data with file references (not file content)
      const submissionData = {
        ...formData,
        documentFiles: fileReferences, // S3 file references instead of base64 content
        uploadInfo: uploadInfo // Information about the upload session
      };

      console.log('Submitting form data to Lambda...');
      console.log('Submission payload size:', JSON.stringify(submissionData).length, 'bytes');

      const response = await fetch(LAMBDA_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Form submission successful:', result);

        setSubmissionResult({
          success: true,
        message: 'Application submitted successfully!',
        applicationId: result.applicationId,
        details: result
      });

      // Clear upload progress
      setUploadProgress(null);
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      setSubmissionResult({
        success: false,
        message: error.message || 'An error occurred while submitting your application.',
        error: error.toString()
      });

      // Clear upload progress on error
      setUploadProgress(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Render upload progress indicator
  const renderUploadProgress = () => {
    if (!uploadProgress) return null;

      return (
      <div className="upload-progress-container">
        <div className="upload-progress-header">
          <h3>{uploadProgress.message}</h3>
          <span className="upload-percentage">{uploadProgress.percentage}%</span>
        </div>
        <div className="upload-progress-bar">
          <div 
            className="upload-progress-fill" 
            style={{ width: `${uploadProgress.percentage}%` }}
          ></div>
        </div>
        {uploadProgress.currentFile && (
          <div className="upload-current-file">
            Uploading: {uploadProgress.currentFile}
          </div>
        )}
        {uploadProgress.completed && uploadProgress.total && (
          <div className="upload-file-count">
            {uploadProgress.completed} of {uploadProgress.total} files completed
          </div>
        )}
        </div>
      );
  };
    
    return (
    <div className="application-form">
      <div className="container">
        {!formType && (
          <>
            <header className="form-header">
              <h1>WWII Veterans Society Application</h1>
              <p className="form-description">
                Join our community dedicated to honoring WWII veterans and preserving their legacy
              </p>
            </header>
            <MembershipOptions onSelectType={handleFormTypeSelection} />
          </>
        )}

        {formType && !submissionResult && (
          <div className="form-container">
        <div className="back-button-container">
          <button 
            className="back-button" 
                onClick={() => handleFormTypeSelection(null)}
                disabled={submitting}
          >
            ← Back to Application Selection
          </button>
        </div>
        
            {/* Show upload progress */}
            {uploadProgress && renderUploadProgress()}

            {formType === 'lineal' && (
              <LinealForm onSubmit={handleSubmit} isSubmitting={submitting} />
            )}
            
            {formType === 'memorial' && (
              <MemorialForm onSubmit={handleSubmit} isSubmitting={submitting} />
        )}
          </div>
        )}

        {submissionResult && (
          <div className={`submission-result ${submissionResult.success ? 'success' : 'error'}`}>
            <div className="result-content">
              <h2>{submissionResult.success ? '✅ Success!' : '❌ Error'}</h2>
              <p>{submissionResult.message}</p>
              
              {submissionResult.success && submissionResult.applicationId && (
                <div className="application-details">
                  <p><strong>Application ID:</strong> {submissionResult.applicationId}</p>
                  <p>Please save this ID for your records.</p>
                </div>
              )}
              
              <button 
                className="restart-button"
                onClick={() => {
                  setSubmissionResult(null);
                  setFormType(null);
                  setUploadProgress(null);
                }}
              >
                Submit Another Application
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationForm; 